/**
 * Create CallFlow Dialog Component
 *
 * Form dialog for creating a new CallFlow with retry configuration.
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { callFlowService, CreateCallFlowData } from '@/services/callFlowService';
import { VariablesEditor, detectVariables } from './VariablesEditor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Loader2, Plus, X } from 'lucide-react';
import { supabase } from '@/supabase/supabase';

interface CreateCallFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  organizationId: string;
}

interface Assistant {
  id: string;
  name: string;
  system_prompt?: string | null;
}

interface FormData extends CreateCallFlowData {
  retry_count: number;
}

export function CreateCallFlowDialog({
  open,
  onOpenChange,
  onSuccess,
  organizationId,
}: CreateCallFlowDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(true);
  const [retryDelays, setRetryDelays] = useState<number[]>([2, 10, 30]);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [detectedVariables, setDetectedVariables] = useState<string[]>([]);

  const form = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      assistant_id: '',
      retry_count: 3,
      retry_delays: [2, 10, 30],
      variables: {},
    },
  });

  useEffect(() => {
    if (open) {
      fetchAssistants();
    }
  }, [open, organizationId]);

  const fetchAssistants = async () => {
    try {
      setLoadingAssistants(true);
      const { data, error } = await supabase
        .from('assistants')
        .select('id, name, system_prompt')
        .eq('organization_id', organizationId)
        .order('name');

      if (error) throw error;
      setAssistants(data || []);
    } catch (error) {
      console.error('Failed to fetch assistants:', error);
      toast({
        title: 'Error',
        description: 'Failed to load assistants',
        variant: 'destructive',
      });
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleAssistantChange = (assistantId: string) => {
    const assistant = assistants.find((a) => a.id === assistantId);
    if (assistant?.system_prompt) {
      setDetectedVariables(detectVariables(assistant.system_prompt));
    } else {
      setDetectedVariables([]);
    }
  };

  const handleRetryCountChange = (count: number) => {
    // Generate default retry delays based on count
    const delays = Array.from({ length: count }, (_, i) => {
      if (i === 0) return 2; // 2 minutes for first retry
      if (i === 1) return 10; // 10 minutes for second retry
      return 30; // 30 minutes for subsequent retries
    });
    setRetryDelays(delays);
  };

  const updateRetryDelay = (index: number, value: number) => {
    const newDelays = [...retryDelays];
    newDelays[index] = value;
    setRetryDelays(newDelays);
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);

      const callFlowData: CreateCallFlowData = {
        name: data.name,
        description: data.description,
        assistant_id: data.assistant_id,
        retry_delays: retryDelays,
        variables,
      };

      await callFlowService.createCallFlow(organizationId, callFlowData);

      toast({
        title: 'Success',
        description: 'CallFlow created successfully',
      });

      form.reset();
      setRetryDelays([2, 10, 30]);
      setVariables({});
      setDetectedVariables([]);
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create CallFlow',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create CallFlow</DialogTitle>
          <DialogDescription>
            Create a new automated batch calling campaign with configurable retry logic.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Q4 Sales Campaign" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Calling all leads for Q4 promotions..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assistant */}
            <FormField
              control={form.control}
              name="assistant_id"
              rules={{ required: 'Assistant is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assistant *</FormLabel>
                  <Select onValueChange={(v) => { field.onChange(v); handleAssistantChange(v); }} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an assistant" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingAssistants ? (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : assistants.length === 0 ? (
                        <div className="px-2 py-1 text-sm text-muted-foreground">
                          No assistants found
                        </div>
                      ) : (
                        assistants.map((assistant) => (
                          <SelectItem key={assistant.id} value={assistant.id}>
                            {assistant.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Prompt Variables */}
            <div className="space-y-3 border rounded-lg p-4 bg-muted">
              <div>
                <h3 className="text-sm font-medium">Prompt Variables</h3>
                <p className="text-xs text-ink-faint mt-0.5">
                  Define values for <code className="font-mono">{'{variable}'}</code> placeholders in your
                  assistant's prompt. Lead fields are filled in automatically per call.
                </p>
              </div>
              <VariablesEditor
                variables={variables}
                onChange={setVariables}
                detectedVariables={detectedVariables}
              />
            </div>

            {/* Retry Configuration */}
            <div className="space-y-4 border rounded-lg p-4 bg-muted">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Retry Configuration</h3>
              </div>

              <FormField
                control={form.control}
                name="retry_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Retries</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        const count = parseInt(value);
                        field.onChange(count);
                        handleRetryCountChange(count);
                      }}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">1 Retry</SelectItem>
                        <SelectItem value="2">2 Retries</SelectItem>
                        <SelectItem value="3">3 Retries</SelectItem>
                        <SelectItem value="4">4 Retries</SelectItem>
                        <SelectItem value="5">5 Retries</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Number of times to retry a call if it fails with eligible reasons (busy,
                      no answer, etc.)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Retry Delays */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Retry Delays (Minutes)</label>
                <p className="text-sm text-muted-foreground">
                  Configure delay in minutes before each retry attempt
                </p>
                <div className="space-y-2">
                  {retryDelays.map((delay, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm w-24">Retry {index + 1}:</span>
                      <Input
                        type="number"
                        min="1"
                        value={delay}
                        onChange={(e) => updateRetryDelay(index, parseInt(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">minutes</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs text-muted-foreground bg-secondary p-2 rounded">
                <strong>Retry-eligible reasons:</strong> busy, no answer, customer ended
                during ring, timeout, network connection lost, failed
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create CallFlow
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
